// =============================================================
// MapLayerCatalog.jsx — basemap + overlay control panel
// -------------------------------------------------------------
// Renders the declarative catalog (constants/mapLayers.js) as a scrollable,
// categorized panel: basemap picker, 3D terrain controls, and per-category
// overlay toggles with opacity sliders. "Planned" layers show greyed with a
// tier badge so the full requested taxonomy is visible while sources are sourced.
// =============================================================
import { useState } from 'react';
import { B } from '../../constants/brand';
import { Icon } from '../ui';
import { BASEMAPS, OVERLAY_CATEGORIES, LAYER_STATS } from '../../constants/mapLayers';

const TIER_BADGE = {
  2: { label: 'Planned',  bg: '#e0f2fe', fg: '#0369a1' },
  3: { label: 'Research', bg: '#fef3c7', fg: '#92400e' },
};

function PlannedRow({ layer }) {
  const badge = TIER_BADGE[layer.tier] || TIER_BADGE[2];
  return (
    <div title={layer.note || ''} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', opacity: 0.6 }}>
      <input type="checkbox" disabled style={{ cursor: 'not-allowed' }} />
      <span style={{ flex: 1, color: B.textMd, fontSize: 12 }}>{layer.label}</span>
      <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: badge.bg, color: badge.fg, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {badge.label}
      </span>
    </div>
  );
}

function OverlayRow({ layer, state, onToggle, onOpacity }) {
  const on      = !!state?.on;
  const opacity = state?.opacity ?? layer.defaultOpacity ?? 0.7;
  return (
    <div style={{ padding: '3px 0' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: B.text }}>
        <input type="checkbox" checked={on} onChange={() => onToggle(layer)} style={{ cursor: 'pointer' }} />
        <span style={{ flex: 1, fontSize: 12 }}>{layer.label}</span>
        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#dcfce7', color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.3 }}>Live</span>
      </label>
      {on && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 2px 22px' }}>
          <span style={{ fontSize: 10, color: B.textLt }}>Opacity</span>
          <input
            type="range" min={0} max={1} step={0.05} value={opacity}
            onChange={e => onOpacity(layer, parseFloat(e.target.value))}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: 10, color: B.textLt, width: 28, textAlign: 'right' }}>{Math.round(opacity * 100)}%</span>
        </div>
      )}
    </div>
  );
}

function Category({ category, overlays, onToggle, onOpacity }) {
  const liveCount    = category.layers.filter(l => l.status === 'live').length;
  const [open, setOpen] = useState(liveCount > 0); // open categories that have something usable
  return (
    <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 6, marginTop: 6 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer', fontFamily: 'inherit' }}
      >
        <span style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd }}>
          {category.label}
        </span>
        <span style={{ fontSize: 11, color: B.textLt, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {liveCount > 0 && <span style={{ color: B.green, fontWeight: 700 }}>{liveCount} live</span>}
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 2 }}>
          {category.layers.map(layer =>
            layer.status === 'live'
              ? <OverlayRow key={layer.id} layer={layer} state={overlays[layer.id]} onToggle={onToggle} onOpacity={onOpacity} />
              : <PlannedRow key={layer.id} layer={layer} />
          )}
        </div>
      )}
    </div>
  );
}

export const MapLayerCatalog = ({
  basemapId, onBasemapChange,
  overlays, onToggleOverlay, onOverlayOpacity,
  terrain, onTerrainToggle, onExaggerationChange,
  style, hideHeader = false,
}) => {
  const liveBasemaps    = BASEMAPS.filter(b => b.status === 'live');
  const plannedBasemaps = BASEMAPS.filter(b => b.status !== 'live');

  return (
    <div style={{
      width: 280, flexShrink: 0, background: B.white, border: `1px solid ${B.border}`,
      borderRadius: 10, padding: 14, overflowY: 'auto', fontSize: 12, color: B.text,
      ...style,
    }}>
      {!hideHeader && (
        <>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Map Layers</div>
          <div style={{ fontSize: 10, color: B.textLt, marginBottom: 10 }}>
            {LAYER_STATS.overlaysLive + LAYER_STATS.basemapsLive} live · {LAYER_STATS.overlaysTotal + LAYER_STATS.basemapsTotal - LAYER_STATS.overlaysLive - LAYER_STATS.basemapsLive} planned
          </div>
        </>
      )}

      {/* ── Basemap ── */}
      <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, marginBottom: 4 }}>Basemap</div>
      {liveBasemaps.map(b => (
        <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer' }}>
          <input type="radio" name="basemap" checked={basemapId === b.id} onChange={() => onBasemapChange(b.id)} style={{ cursor: 'pointer' }} />
          <span style={{ fontSize: 12 }}>{b.label}</span>
        </label>
      ))}
      {plannedBasemaps.map(b => {
        const badge = TIER_BADGE[b.tier] || TIER_BADGE[2];
        return (
          <div key={b.id} title={b.note || ''} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', opacity: 0.6 }}>
            <input type="radio" disabled style={{ cursor: 'not-allowed' }} />
            <span style={{ flex: 1, fontSize: 12, color: B.textMd }}>{b.label}</span>
            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: badge.bg, color: badge.fg, textTransform: 'uppercase', letterSpacing: 0.3 }}>{badge.label}</span>
          </div>
        );
      })}

      {/* ── 3D terrain ── */}
      <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 6, marginTop: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, marginBottom: 4 }}>3D Terrain</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!terrain.on} onChange={() => onTerrainToggle(!terrain.on)} style={{ cursor: 'pointer' }} />
          <span style={{ flex: 1, fontSize: 12 }}>3D Elevation + Hillshade</span>
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#dcfce7', color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.3 }}>Live</span>
        </label>
        {terrain.on && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 2px 22px' }}>
            <span style={{ fontSize: 10, color: B.textLt }}>Exaggeration</span>
            <input type="range" min={1} max={3} step={0.1} value={terrain.exaggeration}
              onChange={e => onExaggerationChange(parseFloat(e.target.value))} style={{ flex: 1 }} />
            <span style={{ fontSize: 10, color: B.textLt, width: 28, textAlign: 'right' }}>{terrain.exaggeration.toFixed(1)}×</span>
          </div>
        )}
      </div>

      {/* ── Overlay categories ── */}
      {OVERLAY_CATEGORIES.map(cat => (
        <Category key={cat.id} category={cat} overlays={overlays} onToggle={onToggleOverlay} onOpacity={onOverlayOpacity} />
      ))}

      <div style={{ borderTop: `1px solid ${B.border}`, marginTop: 8, paddingTop: 8, fontSize: 10, color: B.textLt, lineHeight: 1.4 }}>
        Planned layers list the intended public data source. Wiring them is incremental — see <code>docs/map-layers.md</code>.
      </div>
    </div>
  );
};
